import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { date, dateTime, humanise, num } from '../lib/format';
import { DataTable, useListParams } from '../components/DataTable';
import type { Column } from '../components/DataTable';
import {
  Badge,
  Button,
  Card,
  CardHead,
  Field,
  Input,
  Modal,
  Notice,
  SearchInput,
  Select,
  StatusBadge,
  Textarea,
  useToast,
} from '../components/ui';

interface JobRow {
  id: string;
  title: string;
  status: string;
  viewCount: number;
  liveAt: string | null;
  expiresAt: string | null;
  createdAt: string;
  category: { id: string; name: string };
  location: { id: string; name: string } | null;
  seller: { id: string; mobile: string; displayName: string | null; kycStatus: string };
}

interface JobDetail extends JobRow {
  description: string;
  address: string;
  priceInr: string | null;
  rejectReason: string | null;
  attributes: Array<{ key: string; valueText: string | null; valueNum: number | null; valueBool: boolean | null }>;
}

interface CategoryCount {
  id: string;
  name: string;
  live: number;
  pending: number;
  total: number;
}

/**
 * v2 §21 — vacancies and the people looking for them.
 *
 * These are listings with a `listingType`, not a table of their own: §20–22
 * are explicit that the new sectors are "data rather than new tables". So this
 * is the general listing queue narrowed to two types, and moderation here is
 * the same endpoint the Listings page uses — a job taken down from either
 * screen is taken down once.
 *
 * One side only. 'Job Wanted' — seekers publishing themselves as listings for
 * employers to browse — was removed from the product: a seeker now applies to
 * a vacancy instead, which is a relationship rather than a thing to moderate.
 * The category and its channel are deactivated rather than deleted, so the
 * listings that already existed are hidden rather than orphaned, and any that
 * need moderating are still reachable from the Listings page.
 */
export function JobsPage() {
  const { get, set } = useListParams();
  const [openId, setOpenId] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const toast = useToast();
  const qc = useQueryClient();

  // Vacancies only; the seeking-work side was removed from the product.
  const side = 'JOB_AVAILABLE';
  const search = get('search');
  const status = get('status');
  const categoryId = get('category');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ title: '', description: '', price: '', address: '' });

  // Vacancies per job category, which doubles as the category filter.
  const counts = useQuery({
    queryKey: ['jobs', 'category-counts'],
    queryFn: () => api.get<CategoryCount[]>('/admin/jobs/category-counts'),
  });
  const categories = counts.data ?? [];

  const save = useMutation({
    mutationFn: (v: { id: string; body: Record<string, unknown> }) => api.patch(`/admin/listings/${v.id}`, v.body),
    onSuccess: () => {
      toast('Job saved', 'success');
      setEditing(false);
      qc.invalidateQueries({ queryKey: ['jobs'] });
      qc.invalidateQueries({ queryKey: ['job', openId] });
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const detail = useQuery({
    queryKey: ['job', openId],
    queryFn: () => api.get<JobDetail>(`/admin/listings/${openId}`),
    enabled: Boolean(openId),
  });

  const moderate = useMutation({
    mutationFn: (v: { id: string; status: string; reason?: string }) =>
      api.patch(`/admin/listings/${v.id}/moderate`, { status: v.status, reason: v.reason }),
    onSuccess: () => {
      toast('Job updated', 'success');
      setRejecting(false);
      setReason('');
      setOpenId(null);
      setEditing(false);
      qc.invalidateQueries({ queryKey: ['jobs'] });
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const columns: Array<Column<JobRow>> = [
    {
      key: 'job',
      header: 'Vacancy',
      render: (r) => (
        <div style={{ minWidth: 0 }}>
          <div className="cell-strong cell-clip">{r.title}</div>
          <div className="cell-muted">
            {r.category.name}
            {r.location && ` · ${r.location.name}`}
          </div>
        </div>
      ),
    },
    {
      key: 'poster',
      header: 'Employer',
      render: (r) => (
        <div>
          <Link to={`/users/${r.seller.id}`} onClick={(e) => e.stopPropagation()}>
            {r.seller.displayName ?? r.seller.mobile}
          </Link>
          <div className="cell-muted">
            <StatusBadge value={r.seller.kycStatus} />
          </div>
        </div>
      ),
    },
    { key: 'status', header: 'Status', render: (r) => <StatusBadge value={r.status} /> },
    { key: 'views', header: 'Views', numeric: true, render: (r) => num(r.viewCount) },
    { key: 'expires', header: 'Expires', render: (r) => <span className="cell-muted">{date(r.expiresAt)}</span> },
    { key: 'created', header: 'Posted', render: (r) => <span className="cell-muted">{date(r.createdAt)}</span> },
  ];

  const d = detail.data;

  return (
    <div className="stack">
      <Card>
        <CardHead
          title="Jobs by category"
          sub="Live · waiting for approval · all (not deleted). Click a category to list its jobs."
          action={
            categoryId ? (
              <Button size="sm" onClick={() => set('category', '')}>
                All categories
              </Button>
            ) : undefined
          }
        />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 8 }}>
          {categories.map((c) => (
            <button
              key={c.id}
              className={`chip ${c.id === categoryId ? 'chip--active' : ''}`}
              style={{ justifyContent: 'space-between', display: 'flex', gap: 8, padding: '8px 12px', textAlign: 'left' }}
              onClick={() => set('category', c.id === categoryId ? '' : c.id)}
            >
              <span>{c.name}</span>
              <span className="cell-muted">
                {num(c.live)} · {num(c.pending)} · {num(c.total)}
              </span>
            </button>
          ))}
        </div>
      </Card>

      <DataTable<JobRow>
        queryKey={['jobs', side, search, status, categoryId]}
        path="/admin/listings"
        params={{ listingType: side, search, status, categoryId }}
        columns={columns}
        onRowClick={(r) => setOpenId(r.id)}
        emptyIcon="⚒"
        emptyTitle="No vacancies posted"
        emptyBody="Jobs are listings with a type (v2 §21), so they arrive through the same posting flow as everything else."
        toolbar={
          <>
            <SearchInput value={search} onChange={(v) => set('search', v)} placeholder="Search titles…" />
            <Select
              value={status}
              placeholder="Any status"
              onChange={(e) => set('status', e.target.value)}
              options={['PENDING_REVIEW', 'LIVE', 'DRAFT', 'PENDING_PAYMENT', 'HOLD', 'EXPIRED', 'REJECTED', 'DELETED'].map((v) => ({
                value: v,
                label: v === 'PENDING_REVIEW' ? 'Pending approval' : humanise(v),
              }))}
            />
            <Select
              value={categoryId}
              placeholder="Any category"
              onChange={(e) => set('category', e.target.value)}
              options={categories.map((c) => ({ value: c.id, label: c.name }))}
            />
          </>
        }
      />

      {openId && (
        <Modal
          wide
          title={d?.title ?? 'Job'}
          sub={d ? `${d.category.name}${d.location ? ` · ${d.location.name}` : ''}` : undefined}
          onClose={() => {
            setOpenId(null);
            setRejecting(false);
            setReason('');
            setEditing(false);
          }}
        >
          {d && (
            <div className="stack">
              <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                <StatusBadge value={d.status} />
                <Badge tone="blue">Vacancy</Badge>
                {d.location && <Badge tone="neutral">{d.location.name}</Badge>}
              </div>

              <Field label="Category" hint="Moving a job puts it under the new category for seekers at once.">
                <Select
                  value={d.category.id}
                  disabled={save.isPending}
                  onChange={(e) => save.mutate({ id: d.id, body: { categoryId: e.target.value } })}
                  options={[
                    ...categories.map((c) => ({ value: c.id, label: c.name })),
                    ...(categories.some((c) => c.id === d.category.id) ? [] : [{ value: d.category.id, label: d.category.name }]),
                  ]}
                />
              </Field>

              {editing ? (
                <div className="stack">
                  <Field label="Title">
                    <Input value={draft.title} maxLength={120} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
                  </Field>
                  <Field label="Description">
                    <Textarea rows={5} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
                  </Field>
                  <div className="row row--wrap" style={{ gap: 12 }}>
                    <Field label="Salary from (₹)">
                      <Input type="number" min={0} value={draft.price} onChange={(e) => setDraft({ ...draft, price: e.target.value })} />
                    </Field>
                    <Field label="Location">
                      <Input value={draft.address} onChange={(e) => setDraft({ ...draft, address: e.target.value })} />
                    </Field>
                  </div>
                  <div className="row" style={{ gap: 8 }}>
                    <Button
                      variant="primary"
                      loading={save.isPending}
                      disabled={draft.title.trim().length < 3 || draft.description.trim().length < 10 || draft.address.trim().length < 3}
                      onClick={() =>
                        save.mutate({
                          id: d.id,
                          body: {
                            title: draft.title.trim(),
                            description: draft.description.trim(),
                            address: draft.address.trim(),
                            ...(draft.price.trim() ? { priceInr: Number(draft.price) } : {}),
                          },
                        })
                      }
                    >
                      Save
                    </Button>
                    <Button onClick={() => setEditing(false)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <>
                  <Field label="Description">
                    <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{d.description}</p>
                  </Field>

                  <Field label="Address">{d.address}</Field>
                </>
              )}

              <Field label="Posted by">
                <Link to={`/users/${d.seller.id}`}>{d.seller.displayName ?? d.seller.mobile}</Link>{' '}
                <StatusBadge value={d.seller.kycStatus} />
              </Field>

              {d.attributes.length > 0 && (
                <Field label="Details">
                  <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                    {d.attributes.map((a) => (
                      <Badge key={a.key} tone="neutral">
                        {humanise(a.key)}: {a.valueText ?? a.valueNum ?? String(a.valueBool)}
                      </Badge>
                    ))}
                  </div>
                </Field>
              )}

              <Field label="Timeline">
                <span className="cell-muted">
                  Posted {dateTime(d.createdAt)}
                  {d.liveAt && ` · live ${dateTime(d.liveAt)}`}
                  {d.expiresAt && ` · expires ${date(d.expiresAt)}`}
                </span>
              </Field>

              {d.rejectReason && (
                <Notice tone="warn" title="Previously rejected">
                  {d.rejectReason}
                </Notice>
              )}

              {rejecting ? (
                <Field label="Why is this being taken down?" hint="The poster sees this, so write it for them.">
                  <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
                </Field>
              ) : null}

              <div className="row" style={{ gap: 8 }}>
                {rejecting ? (
                  <>
                    <Button
                      variant="danger"
                      disabled={!reason.trim()}
                      loading={moderate.isPending}
                      onClick={() => moderate.mutate({ id: d.id, status: 'REJECTED', reason: reason.trim() })}
                    >
                      Confirm takedown
                    </Button>
                    <Button variant="ghost" onClick={() => setRejecting(false)}>
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    {d.status !== 'LIVE' && (
                      <Button
                        variant="success"
                        loading={moderate.isPending}
                        onClick={() => moderate.mutate({ id: d.id, status: 'LIVE' })}
                      >
                        Approve
                      </Button>
                    )}
                    {d.status !== 'REJECTED' && (
                      <Button variant="danger" onClick={() => setRejecting(true)}>
                        Reject
                      </Button>
                    )}
                    {!editing && (
                      <Button
                        onClick={() => {
                          setDraft({
                            title: d.title,
                            description: d.description,
                            price: d.priceInr ? String(Math.round(Number(d.priceInr))) : '',
                            address: d.address,
                          });
                          setEditing(true);
                        }}
                      >
                        Edit
                      </Button>
                    )}
                    {d.status !== 'DELETED' && (
                      <Button
                        variant="danger"
                        loading={moderate.isPending}
                        onClick={() => {
                          if (window.confirm('Delete this job? It is taken down everywhere.')) {
                            moderate.mutate({ id: d.id, status: 'DELETED' });
                          }
                        }}
                      >
                        Delete
                      </Button>
                    )}
                    <Link to={`/listings?search=${encodeURIComponent(d.title)}`}>
                      <Button variant="ghost">Open in Listings</Button>
                    </Link>
                  </>
                )}
              </div>
              </div>
          )}
        </Modal>
      )}
    </div>
  );
}
